import React, { useEffect, useState } from "react";
import { Container, Typography, Button, IconButton, Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions } from "@mui/material";
import { TableComponent } from "../../components/Table";
import { ModalPaymentProductComponent } from "../../components";
import { payments } from "../../api/pays";
import { Payment } from "./interface/pay.interface";
import { useNotification } from "../../context/notification.context";
import { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";

export const PaymentsPage: React.FC = () => {
  const [allPayments, setAllPayments] = useState<any[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isModalPaymentOpen, setIsModalPaymentOpen] = useState(false);
  const { getError, getSuccess } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number | null>(null);

  const [proveedor, setProveedor] = useState<string>();
  const [file, setFile] = useState<string | null>(null);
  const [proveedorId, setProveedorId] = useState<number | null>(null);
  const [productId, setProductId] = useState<number | null>(null);
  const [productType, setProductType] = useState<string | null>(null);
  const [cliente, setCliente] = useState<string | null>(null);

  const normalizeCurrency = (currency?: string | null) => {
    if (currency === "USD" || currency === "U$S") return "U$S";
    return "$";
  };

  const getPaymentProduct = (payment: any) => {
    if (payment.hotelPaquete ?? payment.presupuestoHotel) {
      return {
        relation: payment.hotelPaquete ?? payment.presupuestoHotel,
        type: "Alojamiento",
        prefix: "h",
      };
    }
    if (payment.aereoPaquete ?? payment.presupuestoLinea) {
      return {
        relation: payment.aereoPaquete ?? payment.presupuestoLinea,
        type: "Aéreo",
        prefix: "l",
      };
    }
    if (payment.servicioPaquete ?? payment.presupuestoServicio) {
      return {
        relation: payment.servicioPaquete ?? payment.presupuestoServicio,
        type: "Servicio",
        prefix: "s",
      };
    }
    return { relation: null, type: "", prefix: "" };
  };

  const getPackage = (productRelation: any) => {
    return productRelation?.paquete ?? productRelation?.presupuesto ?? null;
  };

  const getClientName = (paquete: any) => {
    return paquete?.cliente?.nombre ?? paquete?.nombre_cliente ?? "";
  };

  const getDisplayAmount = (payment: any, currency: string) => {
    return Number(payment.importe ?? 0);
  };

  const isInternalClosurePayment = (payment: any) => {
    const observation = payment.observacion ?? payment.observaciones ?? "";
    return [
      "Ajuste por saldo menor - 2026",
      "Cierre historico pre-2026",
    ].includes(observation);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await payments.getAll();
        
        const paymentsData = await response.data.filter((payment: any) => !isInternalClosurePayment(payment)).map((payment: any) => {
          const currency = normalizeCurrency(payment.moneda);
          const exchangeRate = Number(payment.valor_de_cambio ?? payment.valorDeCambio ?? 1) || 1;
          const product = getPaymentProduct(payment);
          const productRelation = product.relation;
          const paquete = getPackage(productRelation);
          const productPrefixedId = productRelation?.id ? `${product.prefix}_${productRelation.id}` : "";
          const displayAmount = getDisplayAmount(payment, currency);

          return {
            id: payment.id,
            importeLit: payment.importe ?? 0,
            importe_usd: currency === "U$S" ? displayAmount : undefined,
            importe: currency + " " + formatNumber(displayAmount),
            fecha: new Date(payment.createdAt ?? payment.created_at ?? Date.now()),
            moneda: currency,
            valorDeCambioLit: exchangeRate,
            valorDeCambio: currency === "U$S" ? "$ " + formatNumber(exchangeRate) : "-",
            proveedorId: payment.proveedor?.id ?? payment.proveedore?.id,
            proveedor: payment.proveedor?.nombre ?? payment.proveedore?.nombre ?? "",
            productoId: productPrefixedId,
            productRawId: productRelation?.id ?? null,
            productType: product.type,
            file: productRelation?.nro_file ?? productRelation?.file ?? paquete?.file ?? "",
            cliente: getClientName(paquete),
            observaciones: payment.observacion ?? payment.observaciones ?? "",
            payment: payment
          };
        });
        setAllPayments(paymentsData);
      } catch (error) {
        getError(`Ocurrio un error inesperado al cargar los pagos: ${error}`);
      }
    };
    
    fetchData();
  }, [getError]);

  // Function to format numbers with custom separators
  const formatNumber = (number: any) => {
    return Number(number ?? 0).toLocaleString('es', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  };

  const handleEditModalOpen = (pay: any) => {
    setProveedor(pay.proveedor);
    setFile(pay.file ?? "");
    setProveedorId(pay.proveedorId);
    setProductId(pay.productRawId);
    setProductType(pay.productType);
    setCliente(pay.cliente);

    setSelectedPayment(pay);
    setIsModalPaymentOpen(true);
  };

  const handleDeletePayment = async (id: number) => {
    try {
      await payments.delete(id);
      setAllPayments(
        (prevPayments) =>
          prevPayments?.filter((payment) => payment.id !== id) || null
      );
      getSuccess("Pago eliminado correctamente");
      setDialogOpen(false);
    } catch (error) {
      getError(`Error al eliminar el pago: ${error}`);
    }
  };

  const handleModalPaymentClose = () => {
    setIsModalPaymentOpen(false);
  };

  const handleDialogOpen = (id: number) => {
    setIdToDelete(id);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: "fecha",
      headerName: "Fecha Pago",
      width: 150,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
      type: "date",
      valueGetter: ({ value }) => value,
    },
    {
      field: "importe",
      headerName: "Importe",
      width: 150,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true
    },
    {
      field: "valorDeCambio",
      headerName: "Tipo de Cambio",
      width: 150,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true
    },
    {
      field: "cliente",
      headerName: "Cliente",
      width: 300,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "proveedor",
      headerName: "Proveedor",
      width: 300,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "observaciones",
      headerName: "Observaciones",
      type: "number",
      width: 400,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true
    },    
  ];

  return (
    <Container maxWidth="xl" sx={{ pt: 5}}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        <IconButton aria-label="routeback" size="small" component={Link} to="/">
          <ArrowBackIos fontSize="small" />
        </IconButton>        
        Listado de Pagos
      </Typography> 

      {allPayments ? (
        <TableComponent
          columnData={columns}
          rowData={allPayments}
          actions={true}
          actionsType={"paymentsList"}
          onEdit={handleEditModalOpen}
          onDelete={handleDialogOpen}
          exportTitle={"Listado de Pagos"}
        />
      ) : (
        <Typography variant="body1">Cargando pagos...</Typography>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Eliminar Pago
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Está seguro que desea eliminar el pago?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancelar</Button>
          <Button onClick={() => {handleDeletePayment((idToDelete != null)?idToDelete:0)}} autoFocus>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <ModalPaymentProductComponent
        open={isModalPaymentOpen}
        onClose={handleModalPaymentClose}
        title={(selectedPayment && Object.keys(selectedPayment).length > 0) ? "Editar Pago | " + proveedor + " | File " + file : "Agregar Pago | " + proveedor + " | File " + file}
        providerID={proveedorId ?? 0}
        productType={productType ?? ""}
        productID={productId ?? 0}
        file={file ?? ""}
        cliente={cliente ?? ""}
        initialData={selectedPayment}
      />
      
    </Container>
  );
};
